
import {Vue, Component, Prop} from "vue-property-decorator";
import CompanySubscriptionForm from "@/components/company/CompanySubscriptionForm.vue";
import { CONNECTED_BOX_PROVIDERS } from "@/utils/connectedBoxes";
import { ROWS_PER_PAGE } from "@/utils/constants";

@Component({
    components: {
        CompanySubscriptionForm,
    },
})
export default class CompanySubscription extends Vue {
    @Prop({default: null})
    public company: any;

    private subscriptions: any[] = [];
    private headers: object[] = [];
    private expanded: object[] = [];
    private relaysWithConnectedBoxes: any[] = [];
    private relaysWithMasterlocks: any[] = [];
    private newSubscriptionDialog: boolean = false;
    private newSubscription: object = null;
    private loading: boolean = false;
    private connectedBoxesProviders = CONNECTED_BOX_PROVIDERS;
    private ROWS_PER_PAGE = ROWS_PER_PAGE;

    private async mounted() {
        this.headers = [
            {value: "plan", text: this.$i18n.t("company.plan"), sortable: false},
            {value: "startDate", text: this.$i18n.t("common.startDate"), sortable: false},
            {value: "endDate", text: this.$i18n.t("common.endDate"), sortable: false},
            {value: "data-table-expand" },
        ];
        this.reset();
        this.relaysWithConnectedBoxes = await this.connectedBoxesOfCurrentCompany();
        this.relaysWithMasterlocks = await this.masterlocksOfCurrentCompany();
    }

    private reset() {
        this.expanded = [];
        this.getSubscriptions();
        this.newSubscriptionDialog = false;
        this.newSubscription = {
            Plan: {},
            options: {},
            details: {
                options: {
                    additionalRelays: {
                        initValue: 49,
                        discount: {
                            multiplier: 1,
                        },
                    },
                    connectedBoxes: {
                        initValue: 0,
                        discount: {
                            multiplier: 1,
                        },
                    },
                    masterlocks: {
                        initValue: 0,
                        discount: {
                            multiplier: 1,
                        },
                    },
                    dropKeyInFlowRelaysPackage: {
                        keysNumber: 75,
                        initValue: 100,
                    },
                    dropKeyInFlowRelaysSingle: {
                        initValue: 2,
                        discount: {
                            multiplier: 1,
                        },
                    },
                    manageStock: {
                        initValue: 50,
                        discount: {
                            multiplier: 1,
                        },
                    },
                    allowBooking: {
                        initValue: 50,
                        discount: {
                            multiplier: 1,
                        },
                    },
                    additionalTenKeys: {
                        initValue: 5,
                        number: 0,
                        discount: {
                            multiplier: 1,
                        },
                    },
                    additionalUser: {
                        initValue: 5,
                        number: 0,
                        discount: {
                            multiplier: 1,
                        },
                    },
                },
                planOption: {
                    discount: {
                        multiplier: 1,
                    },
                },
            },
            maxSms: 0,
        };
    }

    private isValidSubscription = (subscription: any) => {
        if (!subscription?.Plan?.id || !subscription.startDate) {
            return false;
        }
        if (subscription.Plan.monthlyPrice > 0 && !subscription?.paymentPeriodicity) {
            return false;
        }
        if (subscription.details.options.dropKeyInFlowRelaysPackage?.isActive
            && !(subscription.details.options.dropKeyInFlowRelaysPackage.keysNumber
                && subscription.details.options.dropKeyInFlowRelaysPackage.initValue)) {
            return false;
        }
        const additionalUserValid: boolean = subscription.details.options.additionalUser.number < 0;
        const additionalTenKeysValid: boolean = subscription.details.options.additionalTenKeys.number < 0;
        if (additionalTenKeysValid || additionalUserValid) {
            return false;
        }
        return true;
    }

    private getSubscriptions() {
        return this.$store.dispatch("admin/companies/getSubscriptions", {
            id: this.company.id,
        })
        .then((res) => {
            // add the currentSubscription at the first index of array
            const currentSubscriptionsIndex: number = res.rows.findIndex(
                (subscription) => !subscription.endDate,
            );
            if (currentSubscriptionsIndex > -1) {
                const currentSubscription: any = res.rows[currentSubscriptionsIndex];
                this.subscriptions = [
                    currentSubscription,
                    ...res.rows,
                ];
                const newIndex: number = currentSubscriptionsIndex + 1;
                this.subscriptions.splice(newIndex, 1);
            } else {
                this.subscriptions = res.rows;
            }
        });
    }

    private createSubscription(subscription: any) {
      this.loading = true;
      this.$store.dispatch("admin/subscriptions/createCompanySubscription", {
            subscription: {
                ...subscription,
                companyId: this.company.id,
                planId: subscription.Plan.id,
            },
        }).then((res) => {
            this.reset();
            this.$store.commit("alerts/displaySuccess", {
                msg: this.$i18n?.t("actions.created"),
            });
        }).catch((err) => {
            this.$store.commit("alerts/displayError", {
                msg: this.$t(`register.error.${err.response.data.type}`),
            });
        }).finally(() => {
          this.loading = false;
        });
    }

    private async updateSubscription(subscription: any) {
        this.loading = true;
        this.$store.dispatch("admin/subscriptions/updateById", {
            id: subscription.id,
            subscription: {
                ...subscription,
                companyId: this.company.id,
                planId: subscription.Plan.id,
            },
        }).then((res) => {
            this.reset();
            this.$store.commit("alerts/displaySuccess", {
                msg: this.$i18n?.t("actions.saved"),
            });
        }).catch((err) => {
            this.$store.commit("alerts/displayError", {
                msg: this.$t(`register.error.${err.response.data.type}`),
            });
        }).finally(() => {
            this.loading = false;
        });
    }

    private async connectedBoxesOfCurrentCompany()  {
        return await Promise.all(this.company.Relays.map(async (relay) => {
            const boxes = (await this.$store.dispatch("buildings/getBoxesExtended",       {
              buildingId : relay.buildingId,
            })).rows;
            const isAcsesLocker = (b) => b?.ConnectedBox?.provider === this.connectedBoxesProviders.ACSES;
            const connectedBoxes: any[] = boxes?.filter((box) => isAcsesLocker(box)) || [];
            return connectedBoxes.length ? {
                ...relay,
                boxes: connectedBoxes,
            } : null;
        })).then((data) => data.filter(Boolean));
    }

  private async masterlocksOfCurrentCompany() {
    return await Promise.all(this.company.Relays.map(async (relay) => {
      const boxes = (await this.$store.dispatch("buildings/getBoxesExtended", {
        buildingId: relay.buildingId,
      })).rows;
      const isMasterlock = (b) => b?.ConnectedBox?.provider === this.connectedBoxesProviders.SNAPACCESS;
      const masterlocks: any[] = boxes.filter((box) => isMasterlock(box)) || [];
      return masterlocks.length ? {
        name: relay.name,
        count: masterlocks.length,
      } : null;
    })).then((data) => data.filter(Boolean));
  }
}
